/**
 * Fremdriftsindikator for KI-studieplangenerering (Fase 2C, task #276/#277)
 *
 * Viser fase-for-fase status under generering. Backend sender NDJSON-events
 * og denne komponenten viser dem som en sjekkliste med animert "spinnings"-status
 * for nåværende fase.
 */

import { CheckCircle2, Loader2, AlertCircle, MinusCircle, Info } from 'lucide-react';

interface PhaseStatus {
  phase: string;
  status: 'started' | 'completed' | 'skipped' | 'failed';
  label?: string;
  details?: string;
}

interface GenerationProgressProps {
  phases: PhaseStatus[];
  isRunning: boolean;
}

// Fasene i kronologisk rekkefølge slik de skal vises i UI
const PHASE_ORDER: { phase: string; defaultLabel: string }[] = [
  { phase: 'pre_analysis',     defaultLabel: 'Analyserer din input' },
  { phase: 'compression',      defaultLabel: 'Kvalitetssjekk av læringsutbytter' },
  { phase: 'complexity_check', defaultLabel: 'Vurderer kompleksitet mot timer og forkunnskap' },
  { phase: 'main_generation',  defaultLabel: 'Genererer studieplan' },
  { phase: 'esco_occupations', defaultLabel: 'Søker yrkesroller i ESCO' },
  { phase: 'esco_skills',      defaultLabel: 'Mapper læringsutbytter til ESCO-ferdigheter' },
  { phase: 'categories',       defaultLabel: 'Foreslår kategorier' },
  { phase: 'finalize',         defaultLabel: 'Ferdigstiller resultatet' }
];

function StatusIcon({ status, isCurrent }: { status: PhaseStatus['status'] | 'pending'; isCurrent: boolean }) {
  if (status === 'completed') return <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />;
  if (status === 'failed')    return <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />;
  if (status === 'skipped')   return <MinusCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />;
  if (status === 'started' || isCurrent) return <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />;
  return <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />;
}

function statusText(p: PhaseStatus): string | null {
  if (p.status === 'completed') {
    // Vis nyttige detaljer per fase
    if (p.phase === 'pre_analysis' && (p as any).existing_outcomes_count !== undefined) {
      const count = (p as any).existing_outcomes_count;
      // Strategy er intern info — vis bare antall læringsutbytter til brukeren
      return `${count} læringsutbytter identifisert i input`;
    }
    if (p.phase === 'compression') {
      const raw = (p as any).raw_count;
      const unique = (p as any).unique_count;
      const merged = (p as any).merged_count;
      if (raw && unique) {
        if (merged > 0 && unique < raw) {
          return `Komprimert ${raw} læringsutbytter til ${unique}`;
        }
        return `Ivaretatt — ingen endringer (${unique} læringsutbytter)`;
      }
    }
    if (p.phase === 'complexity_check') {
      const status = (p as any).status;
      if (status === 'balanced')    return `Balansert kompleksitet`;
      if (status === 'too_complex') return `Kompleksitet over kapasitet for timer/forkunnskap`;
      if (status === 'too_simple')  return `Kapasitet underutnyttet`;
    }
    if (p.phase === 'main_generation' && (p as any).outcomes_total !== undefined) {
      return `${(p as any).outcomes_total} læringsutbytter, ${(p as any).modules_count} moduler`;
    }
    if (p.phase === 'esco_occupations' && (p as any).count !== undefined) {
      return `${(p as any).count} yrkesroller funnet`;
    }
    if (p.phase === 'esco_skills' && (p as any).mappings_count !== undefined) {
      return `${(p as any).mappings_count} læringsutbytter mappet`;
    }
    if (p.phase === 'categories' && (p as any).count !== undefined) {
      return `${(p as any).count} kategorier`;
    }
  }
  if (p.status === 'skipped') {
    const reason = (p as any).reason;
    // Fase-spesifikke, forklarende tekster
    if (p.phase === 'pre_analysis') {
      return 'Ikke nødvendig for denne inputen';
    }
    if (p.phase === 'compression') {
      if (reason === 'too_few_outcomes')             return 'Få læringsutbytter i input — komprimering er ikke relevant';
      if (reason === 'strategy_generate_from_null')  return 'For kort input — KI genererer læringsutbytter direkte';
      if (reason === 'no_output')                    return 'Ingen dublett-utbytter funnet — komprimering ikke nødvendig';
      if (reason === 'compression_failed')           return 'Kunne ikke komprimere — hopper over';
      return 'Ivaretatt — ingen endringer';
    }
    if (p.phase === 'complexity_check') {
      if (reason === 'no_compression_data')  return 'Trenger komprimerte læringsutbytter som grunnlag';
      if (reason === 'fallback_failed')      return 'Kunne ikke vurdere kompleksitet — hopper over';
      if (reason === 'compression_failed')   return 'Krever komprimering først — hopper over';
      return 'Hopper over';
    }
    return 'Hopper over';
  }
  if (p.status === 'failed') {
    return (p as any).error || 'Feilet';
  }
  return null;
}

export default function GenerationProgress({ phases, isRunning }: GenerationProgressProps) {
  // Bygg en map av phase → siste event
  const phaseMap = new Map<string, PhaseStatus>();
  for (const p of phases) {
    const existing = phaseMap.get(p.phase);
    // 'completed' og 'failed' overstyrer 'started'
    if (!existing || p.status === 'completed' || p.status === 'failed' || p.status === 'skipped') {
      phaseMap.set(p.phase, p);
    } else if (existing.status === 'started' && p.status === 'started') {
      // Hold på siste started (med data)
      phaseMap.set(p.phase, p);
    }
  }

  // Finn aktiv fase (siste 'started' uten 'completed'/'failed'/'skipped')
  const activePhase = phases
    .slice()
    .reverse()
    .find(p => p.status === 'started')?.phase;

  // Tell hoppet-over-faser og finn dominerende grunn for kontekst-forklaring
  const skippedPhases = Array.from(phaseMap.values()).filter(p => p.status === 'skipped');
  const skippedCount = skippedPhases.length;
  const dominantReason = (() => {
    if (skippedCount < 2) return null;
    // Se etter typiske grunner
    const reasons = skippedPhases.map(p => (p as any).reason).filter(Boolean);
    if (reasons.includes('strategy_generate_from_null') || reasons.includes('too_few_outcomes')) {
      return 'short_input';
    }
    if (reasons.includes('no_compression_data') || reasons.includes('compression_failed')) {
      return 'cascading';
    }
    return 'other';
  })();

  return (
    <div className="bg-white border border-blue-200 rounded-lg p-5 shadow-sm" role="status" aria-live="polite">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-blue-900">
          {isRunning ? 'KI genererer studieplan…' : 'Generering ferdig'}
        </h3>
        {isRunning && (
          <span className="text-xs text-gray-500">Kan ta 60–120 sekunder</span>
        )}
      </div>

      {/* Kontekst-forklaring når flere faser er hoppet over — dette hjelper
          brukeren å forstå HVORFOR (i stedet for å bare se "hoppet over"). */}
      {dominantReason && (
        <div className="mb-3 bg-blue-50 border border-blue-200 rounded p-3 flex items-start gap-2 text-xs">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-blue-900">
            {dominantReason === 'short_input' && (
              <>
                <strong>Noen kvalitetssjekker er ikke relevante for denne inputen.</strong>
                {' '}Beskrivelsen din er kort eller inneholder få eksplisitte læringsutbytter, så KI-en
                genererer læringsutbytter direkte fra tittel, beskrivelse og timer/kompetansepoeng
                uten forbehandlings-steg. Dette er normal oppførsel — kvalitetssjekkene er designet
                for lange, detaljerte input hvor det er risiko for dubletter eller ubalanse.
              </>
            )}
            {dominantReason === 'cascading' && (
              <>
                <strong>Kvalitetssjekker forutsetter forrige fase.</strong>
                {' '}Kompleksitetsvurdering krever komprimerte læringsutbytter som utgangspunkt.
                Når det trinnet mangler, hopper vi videre til hovedgenereringen.
              </>
            )}
            {dominantReason === 'other' && (
              <>
                <strong>Noen forbehandlings-steg ble hoppet over.</strong>
                {' '}Hovedgenereringen fortsetter uansett — dette påvirker ikke resultatet.
              </>
            )}
          </div>
        </div>
      )}

      {/* Hvis en SENERE fase har status, anta at tidligere fortsatt-spinning er ferdig.
          Dette løser TCP-buffer-bug hvor "completed"-event for en fase kommer i
          samme stream-chunk som "started"-event for neste fase. */}
      <ul className="space-y-2">
        {PHASE_ORDER.map(({ phase, defaultLabel }, idx) => {
          const p = phaseMap.get(phase);
          const isCurrent = phase === activePhase;
          // Sjekk om noen LATER fase har activity → da må denne fasen være ferdig
          const laterPhaseHasActivity = PHASE_ORDER.slice(idx + 1).some(later => {
            const lp = phaseMap.get(later.phase);
            return lp && (lp.status === 'started' || lp.status === 'completed' || lp.status === 'skipped' || lp.status === 'failed');
          });
          let status = p?.status || (isCurrent ? 'started' : 'pending') as any;
          // Auto-promote 'started' til 'completed' hvis en senere fase allerede har activity
          if (status === 'started' && laterPhaseHasActivity) {
            status = 'completed';
          }
          // Bruk alltid frontend sin defaultLabel — backend kan sende labels som
          // "Kvalitetssjekk hoppet over" som skaper redundans med detalj-tekst
          // (både tittel OG detalj sier "hoppet over"). Vi vil at tittelen forblir
          // fasenavnet, mens detalj-tekst forklarer status.
          const label = defaultLabel;
          const detail = p ? statusText(p) : null;

          // Skip render hvis ikke startet enda og ikke aktiv
          if (!p && !isCurrent && !isRunning) return null;

          return (
            <li key={phase} className="flex items-start gap-3 text-sm">
              <StatusIcon status={status} isCurrent={isCurrent} />
              <div className="flex-1 min-w-0">
                <div className={`${
                  status === 'completed' ? 'text-gray-700' :
                  status === 'failed' ? 'text-red-700' :
                  status === 'skipped' ? 'text-gray-500' :
                  status === 'started' ? 'text-blue-700 font-medium' :
                  'text-gray-400'
                }`}>
                  {label}
                </div>
                {detail && (
                  <div className={`text-xs mt-0.5 ${
                    status === 'failed' ? 'text-red-600' :
                    status === 'skipped' ? 'text-gray-400' :
                    'text-gray-500'
                  }`}>
                    {detail}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
